import { useEffect, useMemo, useState } from "react";
import { MaterialReactTable, MRT_Row, type MRT_ColumnDef, type MRT_SortingState } from 'material-react-table';
import dayjs from 'dayjs';
import { Badge, Box, Button, Grid, IconButton, Link, ListItemIcon, Menu, MenuItem, MenuProps, Paper, Tooltip, Typography, alpha, styled } from '@mui/material';
import assets from "../../../assets";
import { useNavigate } from "react-router-dom";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CssBaseline from '@mui/material/CssBaseline';
import { DeviceSessionStorageKey, DeviceSessionStorageModel, DeviceTableParameterStorageService } from "../../../services/sessionStorage";
import RefreshIcon from '@mui/icons-material/Refresh';
import colorConfigs from "../../../configs/colorConfigs";
import { Link as RouterLink } from 'react-router-dom';
import RotateRightOutlinedIcon from '@mui/icons-material/RotateRightOutlined';
import { tsXLXS } from 'ts-xlsx-export';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import DomainAddOutlinedIcon from '@mui/icons-material/DomainAddOutlined';
import { MRT_FilterFns } from 'material-react-table';
import { useMediaQuery } from '@mui/material';
import RareButton from "../buttons/RareButton";
import BallotOutlinedIcon from '@mui/icons-material/BallotOutlined';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import {
  MRT_ToggleGlobalFilterButton,
  MRT_ToggleFiltersButton,
  MRT_ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFullScreenButton,
} from 'material-react-table';

const StyledMenu = styled((props: any) => (
  <Menu data-testid="Menu-8j1c"
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color:
      theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      '&:active': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity,
        ),
      },
    },
  },
}));


interface DeviceTableProps {
  rows: any[];
  isLoading: boolean;
  sorting: any;
  columnVisibility: any;
  columnFilters: any;
  pagination: any;
  globalFilter: any;
  showGlobalFilter: any;
  setShowGlobalFilter: (value: any) => void;
  handleRemoveAllFilters: () => void;
  setGlobalFilter: (value: string) => void;
  setSorting: (sorting: any[]) => void;
  setColumnVisibility: (columnVisibility: { [key: string]: boolean }) => void;
  setColumnFilters: (filters: any[]) => void;
  setPagination: (pagination: { pageSize: number; pageIndex: number }) => void;
  setDevice: (detail: any) => void;
  setPopup: (detail: any) => void;
  handleRefreshButton: () => void;
  openEditPage: (detail: any) => void;
  openCreatePage: () => void;
}

const DeviceTable: React.FC<DeviceTableProps> = ({
  rows,
  isLoading,
  sorting,
  columnVisibility,
  columnFilters,
  pagination,
  globalFilter,
  showGlobalFilter,
  setShowGlobalFilter,
  setGlobalFilter,
  handleRemoveAllFilters,
  setSorting,
  setColumnVisibility,
  setColumnFilters,
  setPagination,
  setDevice,
  setPopup,
  handleRefreshButton,
  openEditPage,
  openCreatePage,
}) => {

  console.log("rows", rows)
  const isMobile = useMediaQuery('(max-width:768px)');

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleSaveFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const customStatusFilter = (row: any, columnId: any, filterValue: any) => {
    const normalize = (str: string) => str.toLowerCase().replace(/_/g, ' ').trim(); // Convert to lowercase, replace underscores with spaces, and trim
    const status = normalize(row.getValue(columnId)?.toString() || ""); // Normalize the status value

    // Normalize filterValue to handle both single strings and arrays
    const normalizedFilterValues = Array.isArray(filterValue)
      ? filterValue.map((value) => normalize(value))
      : [normalize(filterValue)];
    // Check if any of the normalized filter values are partially included in the status
    return normalizedFilterValues.some((value) => status.includes(value));
  };

  const filterFns = {
    ...MRT_FilterFns,
    customStatusFilter, // Add your custom status filter
  };


  const columns: MRT_ColumnDef<any>[] = [
    {
      accessorKey: 'id',
      header: 'ID',
      Header: () => (
        <Box data-testid="Box-v0uc" sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '50px' }}>
          ID<br />&nbsp;
        </Box>
      ),
      Cell: ({ cell }) => (
        <Box data-testid="Box-f9pi" sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: "40px" }}>
          {String(cell.getValue())}
        </Box>
      ),
    },
    {
      accessorKey: 'name',
      header: "Device Name",
      Header: () => <Box data-testid="Box-1fcy" sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '50px' }}>
        Device<br />Name
      </Box>,
    },
    {
      accessorKey: 'model',
      header: "Model",
      Header: () => <Box data-testid="Box-zkob" sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '50px' }}>
        Device<br />Model
      </Box>,
    },
    {
      accessorKey: 'h_factor',
      header: "H Factor",
      Header: () => <Box data-testid="Box-xrsm" sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '50px' }}>
        H<br />Factor
      </Box>,
    },
    {
      accessorKey: 'ultra_wide',
      header: 'Ultra Wide',
      Header: () => <Box data-testid="Box-1e0u" sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '50px' }}>
        Ultra<br />Wide
      </Box>,
      Cell: ({ cell }) => (
        <Box data-testid="Box-7v4c" sx={{ display: "flex", justifyContent: "center" }}>
          {cell.getValue() ? <Typography data-testid="Typography-jk99" fontSize="smaller" sx={{ paddingX: 0.7, border: `1.5px solid ${colorConfigs.buttons.active}`, borderRadius: 2, color: colorConfigs.buttons.active }} >Yes</Typography> : <Typography data-testid="Typography-dqws" fontSize="smaller" sx={{ paddingX: 0.7, border: `1.5px solid ${colorConfigs.buttons.cancel}`, borderRadius: 2, color: colorConfigs.buttons.cancel }} >No</Typography>}
        </Box>
      ),
      filterVariant: 'checkbox',
    },
    {
      accessorKey: 'is_active',
      header: 'Status',
      Header: () => <Box data-testid="Box-esq6" sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '50px' }}>
        Status<br />&nbsp;
      </Box>,
      Cell: ({ cell }) => (
        <Box data-testid="Box-hxso" sx={{ display: "flex", justifyContent: "center" }}>
          {cell.getValue() ? <Typography data-testid="Typography-c5vc" fontSize="smaller" sx={{ paddingX: 0.7, border: `1.5px solid ${colorConfigs.buttons.active}`, borderRadius: 2, color: colorConfigs.buttons.active }} >Active</Typography> : <Typography data-testid="Typography-dqws" fontSize="smaller" sx={{ paddingX: 0.7, border: `1.5px solid ${colorConfigs.buttons.inactive}`, borderRadius: 2, color: colorConfigs.buttons.inactive }} >Inactive</Typography>}
        </Box>
      ),
      filterVariant: 'checkbox',
    },
    // {
    //   accessorFn: (row) => dayjs(row.expiry_date).format('YYYY-MM-DD HH:mm:ss'),
    //   header: 'Expiry Date',
    //   Header: () => <div data-testid="div-3k93" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '50px' }}>
    //     Expiry<br />Date
    //   </div>,
    //   Cell: ({ cell }) => (
    //     <Box data-testid="Box-y3xp" sx={{ whiteSpace: 'pre-wrap' }}>
    //       {cell.getValue()?.toString().split(' ').join('\n')}
    //     </Box>
    //   ),
    // },
  ]


  // Define the type for the keys for handling persistance of table settings in session storage
  type DeviceSessionStorageKeyType = keyof DeviceSessionStorageModel;

  // Generic handler function for persisting table parameters
  const handleParameterChange = <T,>(
    key: DeviceSessionStorageKeyType,
    currentValue: T,
    setValueFunction: (value: T) => void,
    newValue: T | ((prevValue: T) => T)
  ) => {
    const updatedValue = typeof newValue === 'function' ? (newValue as (prevValue: T) => T)(currentValue) : newValue;
    DeviceTableParameterStorageService.set(key, updatedValue);
    setValueFunction(updatedValue);
  };

  // Handlers for persisting table: sorting, pagination, visibility, filter, search, and search bar visibility
  const setSortingFunction = (newSortingValue: any) =>
    handleParameterChange(DeviceSessionStorageKey.sorting, sorting, setSorting, newSortingValue);
  const setPaginationChangeFunction = (newPaginationValue: any) =>
    handleParameterChange(DeviceSessionStorageKey.pagination, pagination, setPagination, newPaginationValue);
  const setFilterChangeFunction = (newFilterChangeValue: any) =>
    handleParameterChange(DeviceSessionStorageKey.columnFilters, columnFilters, setColumnFilters, newFilterChangeValue);
  const setGlobalFilterChangeFunction = (newGlobalFilterChangeValue: string | ((prevValue: string) => string)) =>
    handleParameterChange(DeviceSessionStorageKey.search, globalFilter, setGlobalFilter, newGlobalFilterChangeValue);
  const setShowGlobalFilterChangeFunction = (newShowGlobalFilterChangeValue: any) =>
    handleParameterChange(DeviceSessionStorageKey.showSearch, showGlobalFilter, setShowGlobalFilter, newShowGlobalFilterChangeValue);


  return <MaterialReactTable
    columns={columns}
    data={rows}
    filterFns={filterFns}
    state={{
      isLoading,
      sorting,
      columnFilters,
      pagination,
      globalFilter,
      showGlobalFilter
    }}
    onSortingChange={setSortingFunction}
    onColumnFiltersChange={setFilterChangeFunction}
    onPaginationChange={setPaginationChangeFunction}
    onGlobalFilterChange={setGlobalFilterChangeFunction}
    onShowGlobalFilterChange={setShowGlobalFilterChangeFunction}
    defaultColumn={{
      minSize: 10,
      maxSize: 160,
      size: 10,
    }}
    enableColumnOrdering={true}
    positionActionsColumn="last"
    enableRowSelection
    selectAllMode="all"
    // enableRowActions
    initialState={{
      sorting,
      columnVisibility,
      columnFilters,
      pagination,
      globalFilter,
      density: 'compact',
      columnPinning: { left: ['mrt-row-select'], right: ['mrt-row-actions'] },
    }}

    // TABLE STYLING IS IN THE THEME
    displayColumnDefOptions={{
      'mrt-row-actions': {
        header: "Actions", // Renames Actions Column
      },
      'mrt-row-select': {
        visibleInShowHideMenu: false, //hide the built-in row actions column from the show hide menu
      },
    }}

    muiTopToolbarProps={({ table }) => ({
      sx: (theme: any) => ({
        ...((theme.components?.MuiCustomToolbar?.styleOverrides?.root) || {}),
      }),
    })}

    muiTableHeadProps={{
      sx: (theme: any) => theme.components?.MuiTableHead?.styleOverrides?.root || {},
    }}

    muiTableHeadCellProps={({ column }) => ({
      sx: (theme: any) => ({
        ...(theme.components?.MuiTableCell?.styleOverrides?.head || {}),
        ...(column.id === 'mrt-row-select' && {
          maxWidth: '50px',
          width: '50px',
        }),
      }),
    })}

    muiFilterTextFieldProps={{
      sx: (theme: any) => theme.components?.MuiFilterTextField?.styleOverrides?.root || {},
    }}

    muiTableBodyProps={{
      sx: (theme: any) => theme.components?.MuiTableBody?.styleOverrides?.root || {},
    }}

    muiPaginationProps={{
      rowsPerPageOptions: [250, 500, 1000],
      sx: {
        padding: 0,
        margin: 0,
        '& .MuiTablePagination-root': {
          padding: 0,
          margin: 0,
        },
        '& .MuiToolbar-root': {
          padding: 0,
          margin: 0,
          minHeight: '0px',
        },
        '& .MuiTablePagination-selectLabel': {
          margin: 0,
        },
        '& .MuiTablePagination-displayedRows': {
          margin: 0,
        },
        '& .MuiTablePagination-actions': {
          margin: 0,
        },
      },
    }}

    muiBottomToolbarProps={{
      // BOTTOM TOOLBAR (Pagination)
      sx: {
        height: '41px',
        maxHeight: '41px',
        minHeight: '41px',
        position: 'sticky',
        bottom: 0,
        zIndex: 1,
        backgroundColor: '#fff',
        boxShadow: '0 -2px 4px rgba(0, 0, 0, 0.1)',
        margin: 0,
        padding: 0,
        '& .MuiToolbar-root': {
          padding: 0,
          margin: 0,
          paddingBottom: 0,
          paddingTop: 0,
        },
        '& .MuiBox-root': {
          p: 0,
        },
        '& .MuiIconButton-root': {
          color: colorConfigs.tables.headBg,
        },
        '& .MuiTablePagination-root': {
          margin: 0,
          paddingTop: 0.5,
          paddingBottom: 0.5,
          color: colorConfigs.buttons.bg,
        },
        '& .MuiTablePagination-selectLabel': {
          color: colorConfigs.tables.headBg,
          padding: 0,
          margin: 0,
        },
        '& .MuiTablePagination-displayedRows': {
          color: colorConfigs.buttons.bg,
          padding: 0,
          margin: 0,
        },
        '& .MuiTablePagination-actions': {
          color: colorConfigs.tables.headBg,
          padding: 0,
          margin: 0,
        },
      },
    }}


    muiSelectAllCheckboxProps={{
      'data-testid': 'Select-all-checkbox',
    } as any}

    muiSelectCheckboxProps={({ row }) => ({
      'data-testId': `Select-row-with-devicename-${row.original.name}`,
    } as any)}

    muiTableBodyRowProps={({ row }) => ({
      // slap the test‑id on here
      'data-testid': `Row-with-devicename-${row.original.name}`,

      // your existing handlers and styles
      onClick: (event: any) => {
        console.log(row.original);
        openEditPage(row);
      },
      sx: (theme: any) => ({
        ...(theme.components?.MuiTableBodyRow?.styleOverrides?.root || {}),
        backgroundColor:
          row.index % 2 === 0
            ? colorConfigs.tables.firstRow
            : colorConfigs.tables.secondRow,
      }),
    } as any)}

    muiTableBodyCellProps={({ cell, row }) => ({
      // this creates: e.g. id="email-cell-for-row-42"
      'data-testid': `${cell.column.id}-cell-for-row-with-devicename-${row.original.name}`,

      // preserve any other cell styles
      sx: {},
    } as any)}


    renderToolbarInternalActions={({ table }) => (
      <Box data-testid="Box-028v" sx={{ display: 'flex', alignItems: 'flex-end', gap: '0.5rem' }}>
        {/* 1) Show/Hide Search */}
        <MRT_ToggleGlobalFilterButton
          table={table}
          data-testId={"Toggle-Search" as any}
          sx={{ py: 0.8, px: 1.5, border: '0.5px solid rgb(154, 148, 157)', borderRadius: '4px', boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)" }}

        />

        {/* 2) Show/Hide Filters */}
        <MRT_ToggleFiltersButton
          table={table}
          data-testId={"Toggle-Filters" as any}
          sx={{ py: 0.8, px: 1.5, border: '0.5px solid rgb(154, 148, 157)', borderRadius: '4px', boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)" }}

        />

        {/* 3) Show/Hide Columns */}
        <MRT_ShowHideColumnsButton
          table={table}
          data-testId={"Toggle-Columns" as any}
          sx={{ py: 0.8, px: 1.5, border: '0.5px solid rgb(154, 148, 157)', borderRadius: '4px', boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)" }}
        />

        {/* 4) Show/Hide Density */}
        <MRT_ToggleDensePaddingButton
          table={table}
          data-testId={"Toggle-Density" as any}
          sx={{ py: 0.8, px: 1.5, border: '0.5px solid rgb(154, 148, 157)', borderRadius: '4px', boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)" }}

        />

        {/* 5) Toggle Full Screen */}
        <MRT_ToggleFullScreenButton
          table={table}
          data-testId={"Toggle-Fullscreen" as any}
          sx={{ py: 0.8, px: 1.5, border: '0.5px solid rgb(154, 148, 157)', borderRadius: '4px', boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)" }}

        />
      </Box>
    )}

    renderTopToolbarCustomActions={({ table }) => {

      const handleArchiveCycles = () => {
        table.getSelectedRowModel().flatRows.map(row => {
          //Add archive logic
        });
      };

      const handleExportDeviceRows = (rows: any[]) => {
        const excelRows = [];
        const now = dayjs().format('YYYY-MM-DD HH:mm:ss');
        const filename = `${now}_APIConfigurationsList`;

        for (let x = 0; x < rows.length; x++) {
          const device = rows[x].original;

          excelRows[x] = {
            "ID": device.id,
            "Name": device.name,
            "Model": device.model,
            "H Factor": device.h_factor,
            "Ultra Wide": device.ultra_wide ? "Yes" : "No",
            "Active": device.is_active ? "Yes" : "No",
          };
        }

        tsXLXS().exportAsExcelFile(excelRows).saveAsExcelFile(filename);
      };



      return (

        <Box data-testid="Box-nx6o" sx={{ display: 'flex', gap: '0.3rem', height: "40px", alignItems: 'center' }}>
          <Tooltip data-testid="Tooltip-puz2" title="View action on selected data" placement="top" arrow>
            <Box data-testid="Box-g0ry">
              <RareButton data-testid="RareButton-49a4"
                variant="outlined"
                onClick={handleClick}
                icon={<KeyboardArrowDownIcon sx={{ fontSize: isMobile ? '16px' : '24.5px', p: 0, m: 0, display: 'flex', alignItems: 'center' }} />}
                sx={{ gap: isMobile ? 0 : 1, px: isMobile ? 1 : 2, fontSize: '14px' }}
              >
                {isMobile ? "" : "Bulk Actions"}
              </RareButton>
            </Box>
          </Tooltip>
          <Tooltip data-testid="Tooltip-etes" title="Refresh Data" placement="top" arrow>
            <Box data-testid="Box-grg3">
              <RareButton data-testid="RareButton-7rvk"
                variant="outlined"
                onClick={() => handleRefreshButton()}
                center={true}
                icon={<RefreshIcon sx={{ fontSize: isMobile ? '16px' : '24.5px', p: 0, m: 0, display: 'flex', alignItems: 'center' }} />}
                sx={{ gap: 0, px: isMobile ? 1 : 2 }}
              >
              </RareButton>
            </Box>
          </Tooltip>
          <Tooltip data-testid="Tooltip-2j5q" title="Clear All Table Filters" placement="top" arrow>
            <Box data-testid="Box-4b29">
              <RareButton data-testid="RareButton-0duu"
                variant="outlined"
                onClick={() => handleRemoveAllFilters()}
                icon={<FilterAltOffOutlinedIcon sx={{ fontSize: isMobile ? '16px' : '24.5px', p: 0, m: 0, display: 'flex', alignItems: 'center' }} />}
                sx={{ gap: 0, px: isMobile ? 1 : 2 }}
              >
              </RareButton>
            </Box>
          </Tooltip>
          <Tooltip data-testid="Tooltip-tmtf" title="Create New Phone Configuration" placement="top" arrow>
            <Box data-testid="Box-7azv">
              <RareButton data-testid="RareButton-2te5"
                onClick={openCreatePage}
                variant="success"
                icon={<AssessmentOutlinedIcon sx={{ fontSize: isMobile ? '16px' : '24.5px', p: 0, m: 0, display: 'flex', alignItems: 'center' }} />}
                sx={{ gap: isMobile ? 0 : 1, px: isMobile ? 1 : 2 }}
              >
                {isMobile ? "" : "Create Phone Configuration"}
              </RareButton>
            </Box>
          </Tooltip>
          <StyledMenu
            id="demo-customized-menu"
            MenuListProps={{
              'aria-labelledby': 'demo-customized-button',
            }}
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
          >
            <MenuItem data-testid="MenuItem-tm4k" disabled={
              !table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()
            } onClick={() => handleExportDeviceRows(table.getSelectedRowModel().rows)} disableRipple>
              Export to Excel
            </MenuItem>
          </StyledMenu>
        </Box>
      );
    }}

    renderRowActions={({ row, table }) => (

      <Box data-testid="Box-cgwo" sx={{ gap: '0.5rem', display: "flex" }}>
        {/* <Tooltip data-testid="Tooltip-ztxl"     
                    arrow
                    placement="left"
                    title="Cycles"
                    > */}
        <Badge data-testid="Badge-imkh"
          badgeContent={row.original.total_cycles}
          color={row.original.total_cycles ? 'primary' : 'default'}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          sx={{
            '& .MuiBadge-badge': {
              top: '13px',
              right: '10px',
              background: colorConfigs.tables.headBg,
            },
          }}
          overlap="circular"
        >
          <IconButton data-testid="IconButton-uofi"
            disabled={!row.original.total_cycles}
            component={RouterLink}
            to={`/cycles?user_id=${row.original.id}`}
            state={{ clinicID: row.original.id }}
            sx={{ transition: 'transform 0.3s ease' }}
          >
            <RotateRightOutlinedIcon fontSize="large" sx={{ color: row.original.total_cycles ? colorConfigs.sidebar.color : colorConfigs.sidebar.bg }} />
          </IconButton>
        </Badge>
        {/* </Tooltip> */}
      </Box>
    )}
  />
}

export default DeviceTable;

